<template>
  <v-container class="pa-0" height="100%">
    <MessageDialog
      :show-dialog="showUserIdHasChangedDialog"
      :has-button-two="false"
      type="success"
      :title="$t('L_userid_has_been_updated')"
      :message="
        $t('L_will_now_be_logged_out_and_you_will_have_to_log_back_in_using_your_new_userid')
      "
      :button-one-label="$t('L_OK')"
      :is-persistent="true"
      @buttonOneAction="logUserOut"
    />
    <CardTemplate id="update_userid_card" @show-modal="showDialog">
      <template #icon>
        <v-img
          class="account-img"
          src="./assets/update-username.svg"
          alt=""
          role="presentation"
          width="46px"
          height="46px"
        />
      </template>
      <template #title>
        {{ $t('L_update_username') }} {{ $t('L_parenthesis_email_address') }}
      </template>
      <template #content>
        <p :class="!$vuetify.display.xs ? 'd-flex align-center justify-center mt-4' : 'd-none'">
          <span v-if="customerProfile">
            {{ customerProfile.email }}
          </span>
        </p>
      </template>
      <template #action>
        <OnexButton
          @click="showDialog"
          variant="text"
          id="update_userid_trigger"
          :aria-label="$t('L_update_user_id')"
        >
          {{ $t('L_edit') }}
        </OnexButton>
      </template>
    </CardTemplate>

    <MyAccountDialog
      :header="
        showUserIDPasswordPage ? $t('L_update_user_id') : $t('L_update_user_id_verification')
      "
      :show="showUpdateUserIdDialog"
      @save="nextPage"
      @close="closeDialog"
      :close-is-active="closeIsActive"
      :enable-save-button="enableSaveButton"
      :show-send-grid="getSendGridShowStatus"
      save-changes-btn-id="save_user_id"
      close-btn-id="close_user_id"
      close-btn-class="update_userid_close"
      modal-class="update_userid_modal"
      submit-btn-text="L_next"
    >
      <template v-if="showUserIDPasswordPage">
        <v-form id="form-user-id" ref="formUserId" v-model="formUserIdValid">
          <v-col cols="12" class="mb-2 text-center">
            <div>{{ $t('L_update_user_id_subheading') }}</div>
          </v-col>
          <v-col cols="12" class="mb-2 text-center">
            <span class="font-weight-bold font-size--14">{{
              $t('L_note_you_will_be_signed_out')
            }}</span>
          </v-col>
          <v-col cols="12">
            <div>{{ $t('L_current_user_id') }}</div>
            <div class="emailFont">{{ customerProfile.email }}</div>
          </v-col>
          <v-col cols="12">
            <OnexEmail
              data-comp-id="new_userid_email"
              v-model="emailAddress"
              :label="$t('L_new_user_id')"
              :rules="[v$.emailAddress.notCurrentEmail.$response]"
              required
            />
            <OnexEmail
              data-comp-id="confirm-new_userid_email"
              v-model="confirmEmailAddress"
              :rules="[v$.confirmEmailAddress.notMatchingEmail.$response]"
              :label="$t('L_confirm_new_user_id')"
              required
            />
          </v-col>
          <v-col cols="12">
            <OnexPassword
              :label="$t('L_enter_current_password')"
              required
              v-model="currentPassword"
              data-comp-id="enter_current_password"
            />
          </v-col>
        </v-form>
      </template>
      <template v-else>
        <div v-if="showSendGridPage" class="text-center">
          <v-col cols="12">
            <v-img
              class="sendgridImageWidth"
              alt="SendGrid phone icon"
              src="@/modules/home/myaccount/components/assets/authphone.png"
            />
          </v-col>
          <v-col cols="12" mb-4 class="protect_your_account">
            <span>{{ $t('L_protect_your_account_with_two_factor_authentication') }}</span>
          </v-col>
          <v-col cols="12" mb-4 class="a_verification_code">
            <span>{{ $t('L_a_verification_code_will_be_sent_to_your_email_below') }}</span>
          </v-col>
          <v-col cols="12" mb-4 class="email_blue">
            <v-icon class="right-icon" color="#277dd0" :icon="mdiEmail" />
            {{ $t('L_email_client_email', { customerEmail: emailAddress }) }}
          </v-col>
        </div>
        <div v-else>
          <form @submit.prevent class="text-center">
            <v-col cols="12" class="mb-4">
              <h3 class="heading_three font-weight-bold font-size--15">
                {{ $t('L_authenticate_your_email_address') }}
              </h3>
            </v-col>
            <v-col cols="12" class="mt-4 mb-4 font-weight-regular font-size--14 text-left">
              {{
                $t('L_enter_the_verification_code_we_sent_to_your_email_address', {
                  emailaddress: emailAddress,
                })
              }}
            </v-col>
            <v-row>
              <v-col cols="12" class="mb-4 mt-2">
                <OnexPasscode
                  v-model="otpCode"
                  id="opt-code"
                  :label="$t('L_enter_code')"
                  clear-icon="cancel"
                  class="text-left mb-5"
                  :error-messages="otpErrorMessages"
                  @update:modelValue="checkOTPErrors"
                  @focus="checkOTPErrors"
                />
                <v-row class="font-size--12 text-left mt-3">
                  <v-col cols="12" class="py-0">
                    {{ $t('L_didnt_receive_one') }}
                    <a
                      href="javascript:void(0)"
                      class="text-decoration-none"
                      @click="requestOTPChallenge"
                      >{{ $t('L_resend_code') }}</a
                    >
                  </v-col>
                  <v-col cols="12" class="py-0">
                    {{ $t('L_please_wait_25_seconds') }}
                  </v-col>
                  <v-col cols="12" mb-4 class="py-0">
                    {{ $t('L_check_your_spam_folder_too') }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </form>
        </div>
      </template>
    </MyAccountDialog>
  </v-container>
</template>
<script lang="ts">
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { PasswordInput } from '@/components/Inputs';
import MessageDialog from '@/components/MessageDialog.vue';
import { defineComponent, type ComponentOptions } from 'vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { mapActions, mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { useHomeStore } from '@/modules/home/store/home.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useVuelidate } from '@vuelidate/core';
import CardTemplate from './CardTemplate.vue';
import MyAccountDialog from './MyAccountDialog.vue';
import { mdiCheckCircleOutline, mdiEmail } from '@mdi/js';
import {
  OnexButton,
  OnexEmail,
  OnexLabelTooltip,
  OnexPasscode,
  OnexPassword,
} from '@primerica/onex-ui-lib';
import { $t } from '@/i18n/i18n';
import { RouteNames } from '@/router/routeNames';

interface CustomerProfile {
  email?: string;
  [key: string]: string | undefined;
}

interface NotCurrentEmailValidationVM {
  customerProfile: CustomerProfile;
}

export const notCurrentEmailValidation = (
  value: string,
  vm: NotCurrentEmailValidationVM
): boolean | string => value !== vm.customerProfile.email?.trim() || $t('L_no_current_user_id');
interface NotSameAsEmailValidationVM {
  emailAddress: string;
}

export const notSameAsEmail = (value: string, vm: NotSameAsEmailValidationVM): boolean | string =>
  value.toLowerCase() === vm.emailAddress.toLowerCase() || $t('L_confirm_email_match');

const componentOptions: ComponentOptions = {
  name: 'UpdateUserID',
  components: {
    CardTemplate,
    MyAccountDialog,
    PasswordInput,
    MessageDialog,
    OnexButton,
    OnexLabelTooltip,
    OnexEmail,
    OnexPasscode,
    OnexPassword,
  },
  data: () => ({
    emailAddress: '',
    confirmEmailAddress: '',
    currentPassword: '',
    otpCode: '',
    otpErrorMessages: [] as string[],
    count: 0,
    otpCodeExpired: false,
    mfaToken: '',
    showUpdateUserIdDialog: false,
    closeIsActive: false,
    challengeRequested: false,
    showUserIDPasswordPage: true,
    showSendGridPage: false,
    currentPage: 'userIdPage',
    errorOnRequestUserIdChallenge: false,
    errorOnRequestOTPChallenge: false,
    showUserIdHasChangedDialog: false,
    formUserIdValid: false,
    v$: useVuelidate(),
    mdiCheckCircleOutline,
    mdiEmail,
  }),
  validations() {
    return {
      emailAddress: {
        required,
        notCurrentEmail: notCurrentEmailValidation,
      },
      confirmEmailAddress: {
        notMatchingEmail: notSameAsEmail,
      },
      otpCode: {
        required: requiredIf(this.challengeRequested),
        minLength: minLength(6),
      },
    };
  },
  computed: {
    ...mapStores(useGlobalStore, usePolicyStore, useHomeStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    enableSaveButton() {
      return !this.v$.$invalid && this.count < 3 && !this.otpCodeExpired && this.formUserIdValid;
    },
    getSendGridShowStatus() {
      return !this.showUserIDPasswordPage && this.showSendGridPage;
    },
  },
  methods: {
    ...mapActions(useGlobalStore, { removeAlerts: 'removeAlerts' }),
    ...mapActions(useHomeStore, {
      requestUserIdChallenge: 'requestUserIdChallenge',
      requestChallenge: 'requestChallenge',
      updateUserID: 'updateUserID',
    }),
    showDialog() {
      this.showUpdateUserIdDialog = true;
    },
    clearForm() {
      this.emailAddress = '';
      this.confirmEmailAddress = '';
      this.currentPassword = '';
      this.otpCode = '';
      this.v$.$reset();
    },
    async nextPage() {
      if (this.currentPage === 'userIdPage') {
        await this.requestUserChallenge();
        if (!this.errorOnRequestUserIdChallenge) {
          this.currentPage = 'sendGrid';
          this.showUserIDPasswordPage = false;
          this.showSendGridPage = true;
        }
      } else if (this.currentPage === 'sendGrid') {
        await this.requestOTPChallenge();
        if (!this.errorOnRequestOTPChallenge) {
          this.currentPage = 'verificationPage';
          this.showUserIDPasswordPage = false;
          this.showSendGridPage = false;
        }
      } else if (this.currentPage === 'verificationPage') {
        await this.updateUserID();
      }
    },
    async requestUserChallenge() {
      this.count = 0;
      this.otpErrorMessages = [];
      this.otpCode = '';
      this.globalStore.removeAlerts('payment');
      this.errorOnRequestUserIdChallenge = false;
      await this.requestUserIdChallenge({
        loginId: this.emailAddress,
        password: this.currentPassword,
        // locale: this.locale,
        // token: this.mfaToken,
      })
        .then()
        .catch(() => {
          // alerts puts a banner
          this.errorOnRequestUserIdChallenge = true;
        });
    },
    async requestOTPChallenge() {
      this.count = 0;
      this.otpErrorMessages = [];
      this.otpCode = '';
      this.removeAlerts('payment');
      this.errorOnRequestOTPChallenge = false;
      await this.requestChallenge({
        loginId: this.emailAddress,
        password: this.currentPassword,
        locale: this.locale,
        token: this.mfaToken,
      })
        .then((d: string) => this.setToken(d))
        .catch(() => {
          // alerts puts a banner
          this.errorOnRequestOTPChallenge = true;
        });
    },
    setToken(mfaToken: string) {
      this.challengeRequested = true;
      this.mfaToken = mfaToken;
    },
    async updateUserID() {
      const updateUserID = {
        loginId: this.emailAddress,
        password: this.currentPassword,
        otpCode: this.otpCode,
        token: this.mfaToken,
        locale: this.locale,
      };
      let resUpdateUserId: boolean = false;
      this.removeAlerts('payment');
      try {
        resUpdateUserId = await this.homeStore.updateUserID(updateUserID);
      } catch (e: unknown) {
        this.handleOTPCodeErrors(e);
        this.showUserIdHasChangedDialog = false;
      }
      if (resUpdateUserId) {
        /*
         * Emit custom actions when user successfully saves changes to: UserID
         * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
         */
        const i = this.$dynatrace.enterAction('account_settings_changes_saved_user_id');
        this.$dynatrace.leaveAction(i);
        this.showUserIdHasChangedDialog = true;
      } else {
        this.showUserIdHasChangedDialog = false;
      }
    },
    checkOTPErrors() {
      const validationError = [];
      if (!this.v$.otpCode.required) {
        validationError.push(this.$t('L_otp_required_field'));
      }
      if (!this.v$.otpCode.minLength) {
        validationError.push(this.$t('L_otp_code_min_length'));
      }
      this.otpErrorMessages = validationError;
    },
    handleOTPCodeErrors({
      response,
    }: {
      response: { data?: { appStatusCode?: string } | null } | null;
    }) {
      let error1;
      if (response?.data?.appStatusCode === 'dcp-3003') {
        this.count += 1;
        switch (this.count) {
          case 1:
            error1 = this.$t('L_otp_code_first_attempt'); // messages.otpCode.firstAttempt;
            break;
          case 2:
            error1 = this.$t('L_otp_code_second_attempt'); // messages.otpCode.secondAttempt;
            break;
          case 3:
            error1 = this.$t('L_otp_code_third_attempt'); // messages.otpCode.thirdAttempt;
            break;
          default:
            error1 = this.$t('L_otp_generic_invalid'); // messages.otpCode.otpGenericInvalid;
        }
        this.otpErrorMessages.push(error1);
      }
      if (response?.data?.appStatusCode === 'dcp-3001') {
        this.otpErrorMessages.push(this.$t('L_otp_code_exceeded')); // messages.otpCode.otpCodeExceeded
      }
      if (response?.data?.appStatusCode === 'dcp-3002') {
        this.otpCodeExpired = true;
        this.otpErrorMessages.push(this.$t('L_otp_code_expired')); // messages.otpCode.expired
      }
    },
    async logUserOut() {
      this.closeIsActive = false;
      this.showUserIdHasChangedDialog = false;
      if (CapacitorFacade.isNativePlatform()) {
        await this.$auth.logout(); // ! clear browser session and revoke tokens
        await this.globalStore.resetStore();
        await this.$router.push({ name: RouteNames.POST_LOGOUT });
      } else {
        await this.$auth.logout(); // ! clear browser session and revoke tokens
        await this.globalStore.resetStore();
      }
    },
    resetProcess(strFlag: string) {
      if (strFlag !== 'In page reset') {
        this.globalStore.removeAlerts(strFlag);
        this.clearForm();
      }
      this.count = 0;
      this.otpCodeExpired = false;
      this.challengeRequested = false;
      this.currentPage = 'userIdPage';
      this.showUserIDPasswordPage = true;
      this.showSendGridPage = false;
    },
    closeDialog() {
      this.resetProcess('');
      this.showUpdateUserIdDialog = false;
    },
  },
  watch: {
    async emailAddress() {
      if (this.confirmEmailAddress && this.$refs.formUserId) {
        await this.$refs.formUserId.validate();
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.sendgridImageWidth {
  max-width: 450px;
  width: 90%;
}

.update_user_id {
  font-weight: 600;
  font-size: 18px;
}

.protect_your_account {
  font-weight: 600;
  font-size: 16px;
}

.a_verification_code {
  font-weight: 400;
  font-size: 14px;
}

.email_blue {
  font-weight: bold;
  font-size: 12px;
  color: #277dd0;
}
.emailFont {
  color: #767575;
  font-weight: 400;
  font-size: 14px;
}
</style>
<style lang="scss">
#label-tooltip .label .v-icon__svg {
  display: none !important;
}
</style>
